import {Component, OnDestroy, OnInit} from '@angular/core';
import {Update} from '@ngrx/entity';
import {Observable, Subscription} from 'rxjs';
import {ActivatedRoute, Router} from '@angular/router';
import {SubheaderService} from '../../../../core/_base/layout';
import {AppState} from '../../../../core/reducers';
import {UntypedFormBuilder, UntypedFormGroup, Validators} from '@angular/forms';
import {select, Store} from '@ngrx/store';
import {delay} from 'rxjs/operators';
import {AllAssetClassTypesRequested, AssetClassType, selectAllAssetClassTypes, WallsAndPartitionUpdated} from '../../../../core/linked-tables';
import { TranslateService } from '@ngx-translate/core';

import {
    WallsAndPartition,
    WallsAndPartitionOnServerCreated,
    selectWallsAndPartitionsActionLoading,
    selectWallsAndPartitionById,
    selectLastCreatedWallsAndPartitionId
} from '../../../../core/linked-tables';
import {Location} from '@angular/common';
import { addBuildingAsAssetClass } from '../lt_utils/add_building_ac';
import {combineLatest} from 'rxjs'

@Component({
    selector: 'kt-walls-and-partition-edit',
    templateUrl: './walls-and-partition-edit.component.html',
    styleUrls: ['./walls-and-partition-edit.component.scss']
})


export class WallsAndPartitionEditComponent implements OnInit, OnDestroy {

    model: WallsAndPartition;
    oldModel: WallsAndPartition;

    wallsAndPartitionForm: UntypedFormGroup;
    hasFormErrors = false;

    loading$: Observable<boolean>;
    viewLoading = false;
    loadingAfterSubmit = false;
    mode = 'NEW';
    readonly = false
    propertySubTypes: AssetClassType[] = [];
    // Private properties
    private componentSubscriptions: Subscription;
    private subscriptions: Subscription[] = [];

    /**
     * Component constructor
     *
     * @param activatedRoute: ActivatedRoute
     * @param router: Router
     * @param fb: FormBuilder
     * @param location: Location
     * @param subheaderService: SubheaderService
     * @param store: Store<AppState>
     */
    constructor(private activatedRoute: ActivatedRoute,
                private router: Router,
                private fb: UntypedFormBuilder,
                private location: Location,
                public subheaderService: SubheaderService,
                private store: Store<AppState>,
                private translate: TranslateService) {
    }

    ngOnInit() {
        this.loading$ = this.store.pipe(select(selectWallsAndPartitionsActionLoading));
        const routeSubscription = combineLatest([
            this.activatedRoute.params,
            this.activatedRoute.data
        ]).subscribe(([params, data]) => {
            const id = params.id;
            this.readonly = data.readonly
            if (id && id > 0) {
                this.store.pipe(select(selectWallsAndPartitionById(id))).subscribe(res => {
                    if (res) {
                        this.oldModel = Object.assign({}, res);
                        this.model = Object.assign({}, this.oldModel);
                        this.initWallsAndPartition();
                    }
                });
            } else {
                this.model = new WallsAndPartition();
                this.model.clear();
                this.oldModel = Object.assign({}, this.model);
                this.initWallsAndPartition();
            }
        });
        this.subscriptions.push(routeSubscription);

        this.store.dispatch(new AllAssetClassTypesRequested())
        this.store.select(selectAllAssetClassTypes).subscribe(res => {
          this.propertySubTypes = [];
          if (res) {
            this.propertySubTypes = res.filter(item => item.id == 7 || item.id == 11 || item.id == 17 || item.id == 5 || item.id == 1 || item.id == 3);
            this.propertySubTypes = addBuildingAsAssetClass(this.propertySubTypes);
          }
        })
    }

    ngOnDestroy(): void {
        if (this.componentSubscriptions) {
            this.componentSubscriptions.unsubscribe();
        }
    }

    initWallsAndPartition() {
        this.createForm();
        // set subheaderService
    }


    createForm() {
        this.wallsAndPartitionForm = this.fb.group({
            name: [this.model.name, Validators.required],
            property_sub_type_id: [this.model.property_sub_type_id, Validators.required],
        });
    }

    /**
     * Returns page title
     */
    getComponentTitle(): string {
        if (this.model && this.model.id > 0) {
            this.mode = 'EDIT';
            return `Edit Walls And Partition '${
                this.model.name
                }'`;
        }

        return 'New Walls And Partition';
    }

    /**
     * Check control is invalid
     * @param controlName: string
     */
    isControlInvalid(controlName: string): boolean {
        const control = this.wallsAndPartitionForm.controls[controlName];
        const result = control.invalid && control.touched;
        return result;
    }

    reset() {
        this.model = Object.assign({}, this.oldModel);
        this.createForm();
        this.hasFormErrors = false;
        this.wallsAndPartitionForm.markAsPristine();
        this.wallsAndPartitionForm.markAsUntouched();
        this.wallsAndPartitionForm.updateValueAndValidity();
    }

    prepareWallsAndPartition(): WallsAndPartition {
        const controls = this.wallsAndPartitionForm.controls;
        const _model = new WallsAndPartition();
        _model.clear();
        _model.id = this.model.id;
        _model.name = controls.name.value;
        _model.property_sub_type_id = controls.property_sub_type_id.value;
        _model.is_hidden = this.model.is_hidden

        return _model;
    }

    /** ACTIONS */

    /**
     * On Submit
     */
    onSubmit(withBack: boolean = false) {
        this.hasFormErrors = false;
        const controls = this.wallsAndPartitionForm.controls;
        /** check form */
        if (this.wallsAndPartitionForm.invalid) {
            Object.keys(controls).forEach(controlName =>
                controls[controlName].markAsTouched()
            );

            this.hasFormErrors = true;
            return;
        }

        const editedFacility = this.prepareWallsAndPartition();
        if (this.model.id > 0) {
            this.updateWallsAndPartition(editedFacility);
        } else {
            this.createWallsAndPartition(editedFacility);
        }
    }

    /**
     * Update model
     *
     * @param _model: WallsAndPartitionModel
     */
    updateWallsAndPartition(_model: WallsAndPartition) {
        this.loadingAfterSubmit = true;
        this.viewLoading = true;

        const updateWallsAndPartition: Update<WallsAndPartition> = {
            id: _model.id,
            changes: _model
        };
        this.store.dispatch(new WallsAndPartitionUpdated({
            partialWallsAndPartition: updateWallsAndPartition,
            wallsAndPartition: _model
        }));
        this.oldModel = _model;
        this.navigateToParent();

    }

    /**
     * Create model
     *
     * @param _model: WallsAndPartitionModel
     */
    createWallsAndPartition(_model: WallsAndPartition) {
        this.store.dispatch(new WallsAndPartitionOnServerCreated({wallsAndPartition: _model}));
        this.oldModel = _model;
        this.componentSubscriptions = this.store.pipe(
            select(selectLastCreatedWallsAndPartitionId),
            delay(1000), // Remove this line
        ).subscribe(res => {
            if (!res) {
                return;
            }
            this.navigateToParent();
        });
    }

    /** Alect Close event */
    onAlertClose($event) {
        this.hasFormErrors = false;
    }


    isFormValid() {
        return (this.model && this.model.name && this.model.name.length > 0 &&
            this.wallsAndPartitionForm && this.wallsAndPartitionForm.controls.property_sub_type_id.value >= 0);
    }

    navigateToParent() {
        let url = '../';
        if (this.model.id > 0) {
            url = '../../';
        }
        this.router.navigate([url], {relativeTo: this.activatedRoute});
    }

    canDeactivate() {
        if (this.discard()) {
            if (window.confirm('Are you sure? All unsaved changes will be lost.')) {
                return true;
            } else {
                // ---------work around angular bug--------- reference: https://github.com/angular/angular/issues/13586
                const currentUrlTree = this.router.createUrlTree([], this.activatedRoute.snapshot);
                const currentUrl = currentUrlTree.toString();
                this.location.go(currentUrl);
                // ---------work around end-----------------
                return false;
            }
        }
        return true;
    }

    discard() {
        if (this.model && this.oldModel) {
            return this.model.name != this.oldModel.name;
        }
        return false;
    }

}
